/**
 * ProSidebar.tsx
 * 
 * Redesigned sidebar for the Pro editor.
 * Clean project browser with search, folders, and context menus.
 */

import React, { useState, useMemo, useCallback } from 'react';
import { Search, Plus, Folder, ChevronDown, ChevronRight, MoreHorizontal, Trash2, Copy, Share2, Pencil } from 'lucide-react';
import type { SavedProject } from '../../types';
import type { Project as SidebarProject, Folder as FolderType } from '../../components/SidebarProjectList';
import firebase from 'firebase/compat/app';

interface ProSidebarProps {
    isOpen: boolean;
    onToggle: () => void;
    projects: SidebarProject[];
    folders: FolderType[];
    savedProjects: SavedProject[];
    currentProjectId: string | null;
    onSelectProject: (project: SidebarProject) => void;
    onDeleteProject: (projectId: string) => void;
    onDuplicateProject: (project: SavedProject) => void;
    onRenameProject: (project: SidebarProject, newName: string) => void;
    onShareProject: (project: SidebarProject) => void;
    setFolders: React.Dispatch<React.SetStateAction<FolderType[]>>;
    setRootProjects: React.Dispatch<React.SetStateAction<SidebarProject[]>>;
    user: firebase.User | null;
    exportStatusByProject?: Record<string, { status: 'exporting' | 'uploading' | 'complete' | 'failed'; progress: number; step?: string }>;
}

export const ProSidebar: React.FC<ProSidebarProps> = ({
    isOpen,
    onToggle,
    projects,
    folders,
    savedProjects,
    currentProjectId,
    onSelectProject,
    onDeleteProject,
    onDuplicateProject,
    onRenameProject,
    onShareProject,
    setFolders,
    setRootProjects,
    user,
    exportStatusByProject = {},
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
    const [contextMenuProject, setContextMenuProject] = useState<string | null>(null);
    const [renamingId, setRenamingId] = useState<string | null>(null);
    const [renameValue, setRenameValue] = useState('');

    // Drag & Drop
    const [dragOverRoot, setDragOverRoot] = useState(false);
    const [dragOverFolderId, setDragOverFolderId] = useState<string | null>(null);

    const moveProjectToFolder = useCallback((projectId: string, fromFolderId: string | null, toFolderId: string | null) => {
        if (fromFolderId === toFolderId) return;

        let project: SidebarProject | undefined;
        if (fromFolderId) {
            const folder = folders.find(f => f.id === fromFolderId);
            project = folder?.projects.find(p => p.id === projectId);
        } else {
            project = projects.find(p => p.id === projectId);
        }

        if (!project) return;

        if (fromFolderId) {
            setFolders(prev => prev.map(f => f.id === fromFolderId ? { ...f, projects: f.projects.filter(p => p.id !== projectId) } : f));
        } else {
            setRootProjects(prev => prev.filter(p => p.id !== projectId));
        }

        if (toFolderId) {
            setFolders(prev => prev.map(f => f.id === toFolderId ? { ...f, projects: [project!, ...f.projects] } : f));
            setExpandedFolders(prev => new Set(prev).add(toFolderId));
        } else {
            setRootProjects(prev => [project!, ...prev]);
        }
    }, [folders, projects, setFolders, setRootProjects]);

    const onProjectDragStart = useCallback((e: React.DragEvent, p: SidebarProject, fromFolderId?: string) => {
        e.stopPropagation();
        e.dataTransfer.setData("project-id", p.id);
        if (fromFolderId) e.dataTransfer.setData("from-folder-id", fromFolderId);
    }, []);

    const onRootDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setDragOverRoot(false);
        const movedProjectId = e.dataTransfer.getData("project-id");

        if (movedProjectId) {
            const fromFolderId = e.dataTransfer.getData("from-folder-id") || null;
            if (fromFolderId) moveProjectToFolder(movedProjectId, fromFolderId, null);
        }
    }, [moveProjectToFolder]);

    const onFolderDrop = useCallback((e: React.DragEvent, targetFolderId: string) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverFolderId(null);
        setDragOverRoot(false);
        const movedProjectId = e.dataTransfer.getData("project-id");

        if (movedProjectId) {
            const fromFolderId = e.dataTransfer.getData("from-folder-id") || null;
            if (fromFolderId === targetFolderId) return;
            moveProjectToFolder(movedProjectId, fromFolderId, targetFolderId);
        }
    }, [moveProjectToFolder]);

    const onFolderDragOver = useCallback((e: React.DragEvent, folderId: string) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverFolderId(folderId);
        setDragOverRoot(false);
    }, []);

    const onContentDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setDragOverRoot(true);
        setDragOverFolderId(null);
    }, []);

    const onContentDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setDragOverRoot(false);
    }, []);

    // Filter projects based on search
    const filteredProjects = useMemo(() => {
        if (!searchQuery.trim()) return projects;
        const q = searchQuery.toLowerCase();
        return projects.filter(p => p.title?.toLowerCase().includes(q));
    }, [projects, searchQuery]);

    const filteredFolders = useMemo(() => {
        if (!searchQuery.trim()) return folders;
        const q = searchQuery.toLowerCase();
        return folders.filter(f =>
            f.name.toLowerCase().includes(q) ||
            f.projects.some(p => p.title?.toLowerCase().includes(q))
        );
    }, [folders, searchQuery]);

    const toggleFolder = useCallback((folderId: string) => {
        setExpandedFolders(prev => {
            const next = new Set(prev);
            if (next.has(folderId)) next.delete(folderId);
            else next.add(folderId);
            return next;
        });
    }, []);

    const handleCreateProject = useCallback(() => {
        const newProject: SidebarProject = {
            id: `proj_${Date.now()}`,
            title: 'Nouveau projet',
            assets: [],
            updated: new Date().toLocaleDateString(),
        };
        setRootProjects(prev => [newProject, ...prev]);
        onSelectProject(newProject);
    }, [onSelectProject, setRootProjects]);

    const startRename = useCallback((project: SidebarProject) => {
        setRenamingId(project.id);
        setRenameValue(project.title || '');
        setContextMenuProject(null);
    }, []);

    const submitRename = useCallback((project: SidebarProject) => {
        if (renameValue.trim()) {
            onRenameProject(project, renameValue.trim());
        }
        setRenamingId(null);
    }, [renameValue, onRenameProject]);

    const renderProjectItem = (project: SidebarProject, folderId?: string) => {
        const isActive = project.id === currentProjectId;
        const showContext = contextMenuProject === project.id;

        return (
            <div key={project.id} className="relative">
                <div
                    draggable
                    onDragStart={(e) => onProjectDragStart(e, project, folderId)}
                    className={`pro-sidebar__project ${isActive ? 'active' : ''}`}
                    onClick={() => {
                        if (renamingId !== project.id) {
                            onSelectProject(project);
                        }
                    }}
                >
                    {project.thumbnailUrl ? (
                        <img
                            src={project.thumbnailUrl}
                            alt=""
                            className="pro-sidebar__project-thumb"
                        />
                    ) : (
                        <div className="pro-sidebar__project-thumb" style={{ background: 'var(--pro-bg-active)' }} />
                    )}

                    <div style={{ flex: 1, minWidth: 0 }}>
                        {renamingId === project.id ? (
                            <input
                                autoFocus
                                className="pro-sidebar__search-input"
                                style={{ padding: '2px 4px', fontSize: '12px' }}
                                value={renameValue}
                                onChange={e => setRenameValue(e.target.value)}
                                onBlur={() => submitRename(project)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') submitRename(project);
                                    if (e.key === 'Escape') setRenamingId(null);
                                }}
                            />
                        ) : (
                            <div className="pro-sidebar__project-name">{project.title || 'Sans titre'}</div>
                        )}
                        <div className="pro-sidebar__project-date">
                            {project.updated}
                            {/* Export badge */}
                            {exportStatusByProject[project.id] && (() => {
                                const es = exportStatusByProject[project.id];
                                const isFail = es.status === 'failed';
                                const isDone = es.status === 'complete';
                                const isActive = es.status === 'exporting' || es.status === 'uploading';
                                return (
                                    <span
                                        title={es.step || es.status}
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            marginLeft: 6,
                                            padding: '1px 5px',
                                            borderRadius: 8,
                                            fontSize: 9,
                                            fontWeight: 700,
                                            lineHeight: '14px',
                                            color: '#fff',
                                            background: isFail ? '#ef4444' : isDone ? '#3b82f6' : 'var(--pro-accent)',
                                            animation: isActive ? 'pulse 1.5s ease-in-out infinite' : undefined,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {isFail ? '✗' : isDone ? '✓' : `${Math.round(es.progress)}%`}
                                    </span>
                                );
                            })()}
                        </div>
                    </div>

                    <button
                        className="pro-btn pro-btn--icon"
                        style={{ width: 24, height: 24, flexShrink: 0 }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setContextMenuProject(showContext ? null : project.id);
                        }}
                    >
                        <MoreHorizontal size={12} />
                    </button>
                </div>

                {/* Context menu */}
                {showContext && (
                    <div
                        style={{
                            position: 'absolute',
                            right: 8,
                            top: '100%',
                            background: 'var(--pro-bg-elevated)',
                            border: '1px solid var(--pro-border)',
                            borderRadius: 'var(--pro-radius-md)',
                            padding: '4px',
                            zIndex: 50,
                            minWidth: 160,
                            boxShadow: 'var(--pro-shadow-elevated)'
                        }}
                    >
                        <button
                            className="pro-btn"
                            style={{ width: '100%', justifyContent: 'flex-start', borderRadius: 'var(--pro-radius-sm)' }}
                            onClick={() => startRename(project)}
                        >
                            <Pencil size={12} /> Renommer
                        </button>
                        <button
                            className="pro-btn"
                            style={{ width: '100%', justifyContent: 'flex-start', borderRadius: 'var(--pro-radius-sm)' }}
                            onClick={() => {
                                const full = savedProjects.find(p => p.id === project.id);
                                if (full) onDuplicateProject(full);
                                setContextMenuProject(null);
                            }}
                        >
                            <Copy size={12} /> Dupliquer
                        </button>
                        <button
                            className="pro-btn"
                            style={{ width: '100%', justifyContent: 'flex-start', borderRadius: 'var(--pro-radius-sm)' }}
                            onClick={() => {
                                onShareProject(project);
                                setContextMenuProject(null);
                            }}
                        >
                            <Share2 size={12} /> Partager
                        </button>
                        <div className="pro-divider" />
                        <button
                            className="pro-btn"
                            style={{ width: '100%', justifyContent: 'flex-start', borderRadius: 'var(--pro-radius-sm)', color: '#ef4444' }}
                            onClick={() => {
                                onDeleteProject(project.id);
                                setContextMenuProject(null);
                            }}
                        >
                            <Trash2 size={12} /> Supprimer
                        </button>
                    </div>
                )}
            </div>
        );
    };

    // Close context menu when clicking outside
    React.useEffect(() => {
        if (contextMenuProject) {
            const handler = () => setContextMenuProject(null);
            window.addEventListener('click', handler);
            return () => window.removeEventListener('click', handler);
        }
    }, [contextMenuProject]);

    if (!isOpen) {
        return (
            <div className="pro-sidebar collapsed">
                <div className="pro-sidebar__header" style={{ justifyContent: 'center' }}>
                    <button className="pro-btn pro-btn--icon" onClick={onToggle}>
                        <ChevronRight size={14} />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="pro-sidebar">
            <div className="pro-sidebar__header">
                <span className="pro-sidebar__title">Projets</span>
                <div style={{ display: 'flex', gap: 4 }}>
                    <button className="pro-btn pro-btn--icon" style={{ width: 28, height: 28 }} onClick={handleCreateProject} title="Nouveau projet">
                        <Plus size={14} />
                    </button>
                </div>
            </div>

            {/* Search */}
            <div className="pro-sidebar__search">
                <Search size={12} className="pro-sidebar__search-icon" />
                <input
                    className="pro-sidebar__search-input"
                    placeholder="Rechercher..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                />
            </div>

            {/* Content */}
            <div
                className="pro-sidebar__content"
                onDragOver={onContentDragOver}
                onDragLeave={onContentDragLeave}
                onDrop={onRootDrop}
                style={{
                    ...(dragOverRoot ? {
                        backgroundColor: 'var(--pro-bg-hover)',
                        outline: '2px dashed var(--pro-text-muted)',
                        outlineOffset: '-2px'
                    } : {})
                }}
            >
                {/* Folders */}
                {filteredFolders.map(folder => (
                    <div
                        key={folder.id}
                        className="pro-sidebar__section"
                        onDragOver={(e) => onFolderDragOver(e, folder.id)}
                        onDragLeave={() => setDragOverFolderId(null)}
                        onDrop={(e) => onFolderDrop(e, folder.id)}
                        style={{
                            ...(dragOverFolderId === folder.id ? {
                                backgroundColor: 'var(--pro-bg-hover)',
                                borderRadius: 'var(--pro-radius-md)',
                                outline: '1px dashed var(--pro-text-muted)'
                            } : {})
                        }}
                    >
                        <div
                            className="pro-sidebar__section-header"
                            onClick={() => toggleFolder(folder.id)}
                        >
                            {expandedFolders.has(folder.id) ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                            <Folder size={12} />
                            <span>{folder.name}</span>
                            <span style={{ marginLeft: 'auto', fontSize: '10px', color: 'var(--pro-text-muted)' }}>
                                {folder.projects.length}
                            </span>
                        </div>
                        {expandedFolders.has(folder.id) && (
                            <div style={{ paddingLeft: 8 }}>
                                {folder.projects.map(p => renderProjectItem(p, folder.id))}
                            </div>
                        )}
                    </div>
                ))}

                {/* Root projects */}
                {filteredProjects.length > 0 && (
                    <div className="pro-sidebar__section">
                        {folders.length > 0 && (
                            <div className="pro-sidebar__section-header" style={{ pointerEvents: 'none' }}>
                                <span>Récents</span>
                            </div>
                        )}
                        {filteredProjects.map(p => renderProjectItem(p))}
                    </div>
                )}

                {filteredProjects.length === 0 && filteredFolders.length === 0 && (
                    <div style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--pro-text-muted)' }}>
                        {searchQuery ? 'Aucun résultat' : 'Aucun projet'}
                    </div>
                )}
            </div>
        </div>
    );
};
